import React from 'react';
import { Calendar, Zap, ArrowRight, CheckCircle2, Building2, Users, Target, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { projects, capabilities, statistics, workforce } from '../data/mock';

const Projects = () => {
  return (
    <div className="bg-[#0b0b0b]">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 50px, rgba(255,255,255,0.03) 50px, rgba(255,255,255,0.03) 51px),
                             repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(255,255,255,0.03) 50px, rgba(255,255,255,0.03) 51px)`
          }} />
        </div>
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#b00000] via-red-600 to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#b00000] text-sm font-medium tracking-widest uppercase mb-4 block">Projects & Capabilities</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Proven Track Record of Excellence
            </h1>
            <p className="text-lg text-white/70 leading-relaxed">
              SINCE 1988, we have successfully executed numerous projects across India's leading 
              thermal power plants, delivering reliability, efficiency, and operational excellence.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="py-12 border-t border-white/10 bg-[#b00000]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { value: "SINCE 1988", label: "Year Established" },
              { value: "35+", label: "Years of Experience" },
              { value: "500+", label: "Total Workforce" },
              { value: "25+", label: "Specialized Professionals" }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-[#b00000] mb-1">{stat.value}</div>
                <div className="text-white/60 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Executed Works */}
      <section className="py-20 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#b00000] text-sm font-medium tracking-widest uppercase mb-4 block">Executed Works</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Our Project Portfolio
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              A comprehensive showcase of our projects demonstrating expertise across all aspects of CHP operations.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <Card key={project.id} className="bg-[#0b0b0b] border-white/10 hover:border-[#b00000]/50 transition-all duration-300 group rounded-none none overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-3 text-sm">
                    <div className="flex items-center gap-1 text-[#b00000]">
                      <Calendar className="w-4 h-4" />
                      <span>{project.year}</span>
                    </div>
                    <div className="bg-[#0b0b0b] px-2 py-0.5 text-white/60 text-xs">
                      {project.type}
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{project.title}</h3>
                  <p className="text-white/60 text-sm mb-3">{project.description}</p>
                  <div className="text-[#b00000] text-sm font-medium">
                    Client: {project.client}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-20 border-t border-white/10 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#b00000] text-sm font-medium tracking-widest uppercase mb-4 block">Our Capabilities</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Engineering Excellence Across Disciplines
              </h2>
              <p className="text-white/70 leading-relaxed mb-8">
                Our team of experienced engineers and technicians brings expertise across 
                multiple disciplines, enabling us to deliver comprehensive solutions for 
                complex industrial challenges in the power sector.
              </p>
              
              <div className="grid grid-cols-2 gap-3">
                {capabilities.map((capability, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#b00000] flex-shrink-0" />
                    <span className="text-white/70 text-sm">{capability}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '500+', label: 'Total Workforce' },
                { value: '25+', label: 'Specialized maintenance professionals' },
                { value: '20+', label: 'Years Stacker Reclaimer' },
                { value: '15+', label: 'Years Conveyor Systems' }
              ].map((item, index) => (
                <div key={index} className="bg-[#0b0b0b] border border-white/10 p-6 text-center hover:border-[#b00000]/50 transition-colors">
                  <div className="text-3xl font-bold text-[#b00000] mb-2">{item.value}</div>
                  <div className="text-white/60 text-sm">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Equipment Expertise */}
      <section className="py-20 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#b00000] text-sm font-medium tracking-widest uppercase mb-4 block">Technical Expertise</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Equipment We Service & Manufacture
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              'Wagon Tipplers',
              'Stacker Reclaimers',
              'Belt Conveyors',
              'Apron Feeders',
              'Tripper Trolleys',
              'Hydraulic Systems',
              'Drive Bogies',
              'Support Wheels',
              'Side Beams',
              'Rail Foundations',
              'FGD Systems',
              'Ventilation Systems',
              'Gearboxes',
              'Hydraulic Cylinders',
              'BTG Equipment',
              'Water Treatment Plants'
            ].map((equipment, index) => (
              <div key={index} className="bg-[#0b0b0b] border border-white/10 p-4 text-center hover:border-[#b00000]/50 transition-colors">
                <span className="text-white/70">{equipment}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workshop Capabilities */}
      <section className="py-20 border-t border-white/10 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#b00000] text-sm font-medium tracking-widest uppercase mb-4 block">Manufacturing Capabilities</span>
            <h2 className="text-3xl font-bold text-white mb-4">In-House Workshop</h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Our workshop enables manufacturing and refurbishment of critical CHP components, 
              ensuring quality control and timely delivery.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Building2, title: 'Drive Bogie Manufacturing', desc: 'Complete in-house manufacturing and installation' },
              { icon: Target, title: 'Component Refurbishment', desc: 'Repair and restore worn-out critical components' },
              { icon: Award, title: 'Quality Assurance', desc: 'Rigorous testing and quality control processes' },
              { icon: Users, title: 'Expert Team', desc: '25+ specialized professionals for precision work' }
            ].map((item, index) => (
              <div key={index} className="bg-[#0b0b0b] border border-white/10 p-6 hover:border-[#b00000]/50 transition-colors group">
                <div className="w-12 h-12 bg-[#b00000]/20 flex items-center justify-center mb-4 group-hover:bg-[#b00000] transition-colors">
                  <item.icon className="w-6 h-6 text-[#b00000] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-white/60 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 border-t border-white/10 bg-gradient-to-r from-[#b00000]/10 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Have a Project in Mind?
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto mb-8">
            Let's discuss how our 35+ years of expertise can support your Coal Handling Plant 
            requirements and operational goals.
          </p>
          <Link to="/contact">
            <Button className="bg-[#FFD700] hover:bg-[#FFC000] text-black font-semibold rounded-none none px-8 h-14 text-base transition-all duration-300 group">
              Start a Conversation
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Projects;
